/*
 * AU NOIR custom.js — V7 PERFORMANCE OPTIMIZED / STRICT QUICK VIEW ISOLATION
 * Generated from the 2026-09-21 custom.js supplied in chat.
 *
 * Main optimizations:
 * - Removed duplicate mobile filter module.
 * - Removed initial Quick Size product fetch for every card.
 * - Removed Quick Size full-document MutationObserver.
 * - Quick View now prefetches only the color the user approaches.
 * - Collection-only network patches now install only on collections.
 * - Dynamic styling observers process only added nodes instead of re-scanning the whole page.
 * - Existing customer-facing behavior is otherwise preserved.
 */

/*
=====================================================================
STRETCH + SA VARIANTS — SAFE COLLECTION SYNC
=====================================================================

This version NEVER:
- preventDefault()
- stopPropagation()
- stopImmediatePropagation()
- button.click()
- replace quick-buy-modal

So Stretch's "Select options" button keeps its native click behavior.

What it DOES:
1) Detect the child product selected by SA Variants.
2) Update the EXISTING Stretch quick-buy-modal product-url immediately.
3) Clear any previously-loaded quick-buy HTML so Stretch reloads the
   newly selected child product on the next normal button click.
4) Reset the mobile-only image carousel to slide 1.

Load AFTER theme.js.
Remove ALL previous quick-buy / SA Variants custom JS before using this.
=====================================================================
*/

(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';
  const CARD_SELECTOR = 'product-card';

  const selectedUrlByCard = new WeakMap();
  const originalUrlByCard = new WeakMap();

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;


  /* ================================================================
     URL HELPERS
     ================================================================ */

  function cleanProductUrl(value) {
    if (!value || typeof value !== 'string') return '';

    const rootPath = (() => {
      try {
        return new URL(
          window.Shopify?.routes?.root || '/',
          window.location.origin
        ).pathname.replace(/\/+$/, '');
      } catch (_) {
        return '';
      }
    })();

    try {
      const url = new URL(
        value,
        window.location.origin
      );

      const productIndex =
        url.pathname.indexOf('/products/');

      if (productIndex === -1) {
        return '';
      }

      url.pathname =
        rootPath +
        url.pathname.slice(productIndex);

      return (
        url.pathname +
        url.search +
        url.hash
      );
    } catch (_) {
      return '';
    }
  }

  function handleFromValue(value) {
    if (!value || typeof value !== 'string') return '';

    /*
      Some apps use data-product-handle / data-handle rather than URLs.
      Ignore values that clearly aren't handles.
    */
    if (
      value.includes(' ') ||
      value.includes('/') ||
      value.includes('?') ||
      value.includes('#')
    ) {
      return '';
    }

    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(value)) {
      return '';
    }

    return value;
  }


  /* ================================================================
     GET PRODUCT URL FROM SA VARIANTS ELEMENT
     ================================================================ */

  function productUrlFromElement(startNode, card) {
    if (!startNode || !card) return '';

    let node = startNode.nodeType === 1
      ? startNode
      : startNode.parentElement;

    while (node && node !== card.parentElement) {
      /*
        Normal linked swatch.
      */
      if (node.matches?.('a[href]')) {
        const href = cleanProductUrl(
          node.getAttribute('href') || node.href || ''
        );

        if (href) return href;
      }

      if (node.attributes) {
        for (const attr of node.attributes) {
          const name = attr.name.toLowerCase();
          const value = attr.value || '';

          /*
            Any attribute containing a Shopify product URL.
          */
          const url = cleanProductUrl(value);

          if (url) return url;

          /*
            SA / StarApps may expose just a product handle.
          */
          if (
            name.includes('product-handle') ||
            name === 'data-handle' ||
            name.endsWith('-handle')
          ) {
            const handle = handleFromValue(value);

            if (handle) {
              return cleanProductUrl(`/products/${handle}`);
            }
          }
        }
      }

      /*
        Sometimes the actual product <a> is nested inside the swatch.
      */
      const nested = node.querySelector?.('a[href*="/products/"]');

      if (nested) {
        const href = cleanProductUrl(
          nested.getAttribute('href') || nested.href || ''
        );

        if (href) return href;
      }

      if (node === card) break;

      node = node.parentElement;
    }

    return '';
  }


  /* ================================================================
     READ CURRENT PRODUCT URL FROM STRETCH CARD
     ================================================================ */

  function currentCardUrl(card) {
    if (!card) return '';

    const selectors = [
      '.product-title[href*="/products/"]',
      '.product-card__media[href*="/products/"]',
      'a[href*="/products/"]'
    ];

    for (const selector of selectors) {
      const link = card.querySelector(selector);

      if (!link) continue;

      const url = cleanProductUrl(
        link.getAttribute('href') || link.href || ''
      );

      if (url) return url;
    }

    const handle = card.getAttribute('handle');

    return handle ? cleanProductUrl(`/products/${handle}`) : '';
  }


  function localizeCardProductUrls(card) {
    if (!card) return;

    card
      .querySelectorAll(
        'a[href*="/products/"], quick-buy-modal[product-url], [data-product-url], [data-url]'
      )
      .forEach(node => {
        [
          'href',
          'product-url',
          'data-product-url',
          'data-url'
        ].forEach(attribute => {
          const value =
            node.getAttribute?.(attribute);

          if (
            !value ||
            !value.includes('/products/')
          ) {
            return;
          }

          const localized =
            cleanProductUrl(value);

          if (
            localized &&
            localized !== value
          ) {
            node.setAttribute(
              attribute,
              localized
            );
          }
        });
      });
  }


  function rememberOriginal(card) {
    if (!card || originalUrlByCard.has(card)) return;

    const url = currentCardUrl(card);

    if (url) {
      originalUrlByCard.set(card, url);
    }
  }


  /* ================================================================
     UPDATE STRETCH QUICK BUY WITHOUT TOUCHING THE BUTTON
     ================================================================ */

  function syncQuickBuyModal(card, productUrl) {
    if (!card) return;

    const clean = cleanProductUrl(productUrl);

    if (!clean) return;

    selectedUrlByCard.set(card, clean);
    card.dataset.saSelectedProductUrl = clean;

    const quickBuyButton = card.querySelector(
      '.product-card__quick-buy [aria-controls]'
    );

    if (!quickBuyButton) return;

    const modalId = quickBuyButton.getAttribute('aria-controls');

    if (!modalId) return;

    const modal = document.getElementById(modalId);

    if (
      !modal ||
      modal.tagName.toLowerCase() !== 'quick-buy-modal'
    ) {
      return;
    }

    /*
      CRITICAL MODAL ISOLATION:
      Once Quick View is open, this COLLECTION-CARD synchronizer must not
      rewrite product-url or clear the modal. The open Quick View has its own
      switching controller below. Without this guard, mutations created by a
      Quick View product swap can wake this collection observer and restore
      the card's previous product into the modal.
    */
    if (modal.hasAttribute('open')) {
      return;
    }

    const current = cleanProductUrl(
      modal.getAttribute('product-url') || ''
    );

    if (current !== clean) {
      /*
        KEY DIFFERENCE FROM THE OLD CODE:
        Keep the SAME custom element in the DOM.
        Only change the URL Stretch will load.
      */
      modal.setAttribute('product-url', clean);

      /*
        Stretch fills this modal dynamically.
        Clearing stale content forces the next NORMAL Select options click
        to load the newly selected SA child product.
      */
      if (modal.innerHTML.trim() !== '') {
        modal.innerHTML = '';
      }

      /*
        Remove common "already loaded" markers if present.
        These are harmless if Stretch doesn't use them.
      */
      modal.removeAttribute('loaded');
      modal.removeAttribute('data-loaded');
      modal.removeAttribute('data-product-loaded');
    }
  }


  /* ================================================================
     MOBILE CAROUSEL
     ================================================================ */

  function resetMobileGallery(card) {
    if (!isMobile() || !card) return;

    const gallery = card.querySelector(
      '.product-card__media--gallery'
    );

    if (!gallery) return;

    requestAnimationFrame(() => {
      try {
        gallery.scrollTo({
          left: 0,
          behavior: 'auto'
        });
      } catch (_) {
        gallery.scrollLeft = 0;
      }
    });
  }


  /* ================================================================
     DETECT SA VARIANTS INTERACTION
     ================================================================ */

  function looksLikeVariantInteraction(target, card) {
    if (!target || !card) return false;

    /*
      IMPORTANT:
      Collection-card syncing must NEVER react to swatches inside an open
      Quick View. Quick View has its own isolated switching system below.
      Without this guard, the collection-card module can rewrite/clear the
      modal while the Quick View module is switching products.
    */
    if (target.closest?.('quick-buy-modal')) {
      return false;
    }

    /*
      Never treat Stretch's own Quick Buy or product links as SA swatches.
    */
    if (
      target.closest('.product-card__quick-buy') ||
      target.closest('.product-card__media') ||
      target.closest('.product-title')
    ) {
      return false;
    }

    let node = target.nodeType === 1
      ? target
      : target.parentElement;

    while (node && node !== card.parentElement) {
      const signature = [
        node.id || '',
        typeof node.className === 'string'
          ? node.className
          : '',
        ...Array.from(node.attributes || []).map(
          attr => `${attr.name}=${attr.value}`
        )
      ].join(' ').toLowerCase();

      if (
        signature.includes('variant') ||
        signature.includes('swatch') ||
        signature.includes('starapp') ||
        signature.includes('sa-variant') ||
        signature.includes('product-group')
      ) {
        return true;
      }

      if (node === card) break;

      node = node.parentElement;
    }

    return Boolean(productUrlFromElement(target, card));
  }


  function processVariantInteraction(target) {
    const card = target?.closest?.(CARD_SELECTOR);

    if (!card) return;
    if (!looksLikeVariantInteraction(target, card)) return;

    rememberOriginal(card);

    /*
      Best case:
      the SA Variants swatch directly tells us the child product URL.
    */
    const directUrl = productUrlFromElement(target, card);

    if (directUrl) {
      syncQuickBuyModal(card, directUrl);
    }

    /*
      SA Variants may update Stretch's title/media link asynchronously.
      Re-check a few times AFTER the app has processed the swatch.

      If we already have a direct child-product URL, don't overwrite it
      with the original card URL.
    */
    [30, 100, 250, 500].forEach(delay => {
      setTimeout(() => {
        localizeCardProductUrls(card);

        const current = currentCardUrl(card);
        const original = originalUrlByCard.get(card) || '';
        const selected =
          selectedUrlByCard.get(card) ||
          card.dataset.saSelectedProductUrl ||
          '';

        if (
          current &&
          (
            !selected ||
            current !== original
          )
        ) {
          syncQuickBuyModal(card, current);
        }

        resetMobileGallery(card);
      }, delay);
    });
  }


  /* ================================================================
     EVENTS — NOTICE THERE IS NO QUICK-BUY BUTTON HANDLER
     ================================================================ */

  document.addEventListener(
    'pointerdown',
    event => {
      const card = event.target.closest?.(CARD_SELECTOR);

      if (!card) return;

      if (looksLikeVariantInteraction(event.target, card)) {
        rememberOriginal(card);

        const directUrl =
          productUrlFromElement(event.target, card);

        if (directUrl) {
          syncQuickBuyModal(card, directUrl);
        }
      }
    },
    true
  );


  document.addEventListener(
    'click',
    event => {
      processVariantInteraction(event.target);
    },
    true
  );


  document.addEventListener(
    'change',
    event => {
      processVariantInteraction(event.target);
    },
    true
  );


  /* ================================================================
     WATCH CHANGES MADE BY SA VARIANTS
     ================================================================ */

  const observer = new MutationObserver(mutations => {
    const cards = new Set();

    for (const mutation of mutations) {
      const element = mutation.target.nodeType === 1
        ? mutation.target
        : mutation.target.parentElement;

      /*
        Never let collection synchronization react to DOM work happening
        inside Quick View. A quick-buy-modal is often nested under its
        product-card, so closest('product-card') alone is not enough.
      */
      if (element?.closest?.('quick-buy-modal')) {
        continue;
      }

      const card = element?.closest?.(CARD_SELECTOR);

      if (card) cards.add(card);
    }

    cards.forEach(card => {
      localizeCardProductUrls(card);

      /*
        While this card's Quick View is open, the modal owns product state.
        Do not push collection-card state back into it.
      */
      if (card.querySelector?.('quick-buy-modal[open]')) {
        return;
      }

      rememberOriginal(card);

      const current = currentCardUrl(card);
      const original = originalUrlByCard.get(card) || '';

      if (current && current !== original) {
        syncQuickBuyModal(card, current);
        resetMobileGallery(card);
      }
    });
  });


  function startObserver() {
    const root =
      document.querySelector('main') ||
      document.body;

    if (!root) return;

    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'href',
        'handle',
        'data-url',
        'data-product-url',
        'data-product-handle',
        'data-handle'
      ]
    });
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }

})();

/* ============================================================
   AU NOIR — SV VARIANTS COLLECTION
   PRICE + SALE BADGE SYNC

   SAFE VERSION
   - No MutationObserver
   - No preventDefault
   - No click simulation
   - No Quick Buy interference
   ============================================================ */

(() => {
  'use strict';

  const SWATCH_SELECTOR = [
    '.swatch-group-selector',
    '.star-set-image',
    '.swatch-view-item'
  ].join(',');

  const CACHE = new Map();

  const CURRENT_PRODUCT =
    new WeakMap();


  const PRICE_LOCK_CLASS =
    'au-market-price-lock';

  function ensurePriceLockStyles() {
    if (
      document.getElementById(
        'au-market-price-lock-style'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'au-market-price-lock-style';

    style.textContent =
      'product-card[data-au-market-price-pending="true"] .product-card__info price-list:not(.au-market-price-lock){visibility:hidden!important}' +
      'product-card .au-market-price-lock{visibility:visible!important;pointer-events:none!important}' +
      'product-card[data-au-market-price-pending="true"] .boss-quick-size__button,' +
      'product-card[data-au-market-price-pending="true"] .boss-quick-shop__size,' +
      'product-card[data-au-market-price-pending="true"] .product-card__quick-buy button' +
      '{pointer-events:none!important;opacity:.55!important;}';

    document.head.appendChild(style);
  }

  function beginPriceLock(card) {
    if (!card) return;

    ensurePriceLockStyles();

    if (
      card.dataset.auMarketPricePending ===
      'true'
    ) {
      return;
    }

    const priceList =
      card.querySelector(
        '.product-card__info price-list:not(.au-market-price-lock)'
      );

    if (!priceList) return;

    const parent =
      priceList.parentElement;

    if (!parent) return;

    if (
      window.getComputedStyle(parent).position ===
      'static'
    ) {
      parent.style.position =
        'relative';
    }

    const parentRect =
      parent.getBoundingClientRect();

    const priceRect =
      priceList.getBoundingClientRect();

    const clone =
      priceList.cloneNode(true);

    clone.classList.add(
      PRICE_LOCK_CLASS
    );

    clone.setAttribute(
      'aria-hidden',
      'true'
    );

    clone.style.position =
      'absolute';

    clone.style.left =
      (priceRect.left -
        parentRect.left) +
      'px';

    clone.style.top =
      (priceRect.top -
        parentRect.top) +
      'px';

    clone.style.zIndex =
      '20';

    clone.style.whiteSpace =
      'nowrap';

    parent.appendChild(clone);

    card.dataset.auMarketPricePending =
      'true';
  }

  function releasePriceLock(card) {
    if (!card) return;

    card
      .querySelectorAll(
        '.' + PRICE_LOCK_CLASS
      )
      .forEach(
        node => node.remove()
      );

    delete card.dataset
      .auMarketPricePending;
  }

  function releasePriceLockAfterPaint(
    card
  ) {
    requestAnimationFrame(
      () => {
        requestAnimationFrame(
          () => releasePriceLock(card)
        );
      }
    );
  }


  /* ============================================================
     CLEAN PRODUCT URL
     ============================================================ */

  function cleanUrl(value) {
    if (!value) return '';

    const rootPath = (() => {
      try {
        return new URL(
          window.Shopify?.routes?.root || '/',
          window.location.origin
        ).pathname.replace(/\/+$/, '');
      } catch (_) {
        return '';
      }
    })();

    try {
      const url =
        new URL(
          value,
          window.location.origin
        );

      const productIndex =
        url.pathname.indexOf(
          '/products/'
        );

      if (productIndex === -1) {
        return '';
      }

      url.pathname =
        rootPath +
        url.pathname.slice(
          productIndex
        );

      const variant =
        url.searchParams.get(
          'variant'
        );

      url.search = '';

      if (variant) {
        url.searchParams.set(
          'variant',
          variant
        );
      }

      return (
        url.pathname +
        url.search
      );

    } catch (_) {
      return '';
    }
  }


  function productUrlFromSwatch(
    swatch
  ) {
    if (!swatch) return '';

    const directValues = [
      swatch.getAttribute?.(
        'swatch-url'
      ),
      swatch.getAttribute?.(
        'data-product-url'
      ),
      swatch.getAttribute?.(
        'data-url'
      ),
      swatch.getAttribute?.(
        'href'
      )
    ];

    for (
      const value of directValues
    ) {
      const clean =
        cleanUrl(value);

      if (clean) {
        return clean;
      }
    }

    const nested =
      swatch.querySelector?.(
        '[swatch-url], ' +
        '[data-product-url], ' +
        '[data-url], ' +
        'a[href*="/products/"]'
      );

    if (
      nested &&
      nested !== swatch
    ) {
      return productUrlFromSwatch(
        nested
      );
    }

    const handle =
      swatch.getAttribute?.(
        'data-product-handle'
      ) ||
      swatch.getAttribute?.(
        'data-handle'
      );

    if (
      handle &&
      /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(
        handle
      )
    ) {
      return cleanUrl(
        '/products/' +
        handle
      );
    }

    return '';
  }


  /* ============================================================
     MONEY FROM BADGE
     ============================================================ */

  function getBadgeHtmlFromDocument(
    doc
  ) {

    /*
      Look for Stretch's real sale badge
      anywhere on fetched product HTML.
    */

    const badge =
      doc.querySelector(
        'on-sale-badge'
      );


    if (!badge) {
      return '';
    }


    return badge.innerHTML;

  }



  /* ============================================================
     PRICE HTML CLEANUP

     Stretch / translated price markup can sometimes output:
     <span class="sr-only">Selling price</span>: €94.95

     The colon is a real text node after the accessibility label.
     Remove ONLY that leading colon. Never touch the price digits.
     ============================================================ */

  function cleanPriceHtml(
    priceList
  ) {

    if (!priceList) {
      return '';
    }


    const clone =
      priceList.cloneNode(true);


    clone
      .querySelectorAll(
        'sale-price, compare-at-price'
      )
      .forEach(priceElement => {

        const srOnly =
          priceElement.querySelector(
            ':scope > .sr-only'
          );


        if (!srOnly) {
          return;
        }


        let node =
          srOnly.nextSibling;


        while (
          node &&
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.trim() === ''
        ) {
          node = node.nextSibling;
        }


        if (
          node &&
          node.nodeType === Node.TEXT_NODE
        ) {

          node.textContent =
            node.textContent.replace(
              /^\s*:\s*/,
              ''
            );

        }

      });


    return clone.innerHTML;

  }



  /* ============================================================
     GET PRICE FROM STRETCH HTML
     ============================================================ */

  function getPriceHtmlFromDocument(
    doc
  ) {

    /*
      First try Stretch Quick Buy.
    */

    const template =
      doc.getElementById(
        'quick-buy-content'
      );


    if (
      template &&
      template.content
    ) {

      const price =
        template.content.querySelector(
          'price-list'
        );


      if (price) {

        return cleanPriceHtml(
          price
        );

      }

    }


    /*
      Product page fallback.
    */

    const productPrice =
      doc.querySelector(
        '.product-info__block-item[data-block-type="price"] price-list'
      ) ||

      doc.querySelector(
        '.product-info price-list'
      );


    if (productPrice) {

      return cleanPriceHtml(
        productPrice
      );

    }


    return '';

  }



  /* ============================================================
     DETECT SALE USING PRICE MARKUP
     ============================================================ */

  function priceIsOnSale(
    priceHtml
  ) {

    if (!priceHtml) {
      return false;
    }


    const holder =
      document.createElement(
        'div'
      );


    holder.innerHTML =
      `<price-list>${priceHtml}</price-list>`;


    /*
      Stretch normally outputs compare-at-price
      when product is discounted.
    */

    return Boolean(

      holder.querySelector(
        'compare-at-price'
      ) ||

      holder.querySelector(
        'del'
      ) ||

      holder.querySelector(
        '.price-list--on-sale'
      )

    );

  }



  /* ============================================================
     GET SAVINGS AMOUNT FROM PRICE HTML
     ============================================================ */

  function getSavingsText(
    priceHtml
  ) {

    if (!priceHtml) {
      return '';
    }


    const holder =
      document.createElement(
        'div'
      );


    holder.innerHTML =
      `<price-list>${priceHtml}</price-list>`;


    const saleElement =
      holder.querySelector(
        'sale-price .money'
      );


    const compareElement =
      holder.querySelector(
        'compare-at-price .money'
      );


    if (
      !saleElement ||
      !compareElement
    ) {
      return '';
    }


    const parseMoney =
      text => {

        if (!text) {
          return NaN;
        }


        let cleaned =
          text
            .replace(
              /[^0-9,.\-]/g,
              ''
            );


        /*
          Canadian/US formatting:
          1,299.00
        */

        if (
          cleaned.includes(',') &&
          cleaned.includes('.')
        ) {

          cleaned =
            cleaned.replace(
              /,/g,
              ''
            );

        }

        /*
          Handle comma decimal if needed.
        */

        else if (
          cleaned.includes(',') &&
          !cleaned.includes('.')
        ) {

          const pieces =
            cleaned.split(',');


          if (
            pieces[
              pieces.length - 1
            ].length === 2
          ) {

            cleaned =
              cleaned.replace(
                ',',
                '.'
              );

          } else {

            cleaned =
              cleaned.replace(
                /,/g,
                ''
              );

          }

        }


        return parseFloat(
          cleaned
        );

      };


    const sale =
      parseMoney(
        saleElement.textContent
      );


    const compare =
      parseMoney(
        compareElement.textContent
      );


    if (
      !Number.isFinite(sale) ||
      !Number.isFinite(compare) ||
      compare <= sale
    ) {

      return '';

    }


    const savings =
      compare - sale;


    /*
      Keep whole dollar when possible.
    */

    const formatted =
      Number.isInteger(savings)

        ? savings.toFixed(0)

        : savings.toFixed(2);


    /*
      Detect currency symbol from
      Stretch's rendered money.
    */

    const saleText =
      saleElement.textContent
        .trim();


    let symbol = '$';


    if (
      saleText.includes('€')
    ) {

      symbol = '€';

    }

    else if (
      saleText.includes('£')
    ) {

      symbol = '£';

    }


    return (
      symbol +
      formatted
    );

  }



  /* ============================================================
     FETCH SELECTED SV PRODUCT
     ============================================================ */

  async function getProductData(
    productUrl
  ) {

    const url =
      cleanUrl(
        productUrl
      );


    if (!url) {
      return null;
    }


    if (
      CACHE.has(url)
    ) {

      return CACHE.get(
        url
      );

    }


    const request =
      fetch(
        url,
        {
          credentials:
            'same-origin'
        }
      )

        .then(
          response => {

            if (
              !response.ok
            ) {

              throw new Error(
                'Product request failed: ' +
                response.status
              );

            }


            return response.text();

          }
        )


        .then(
          html => {

            const doc =
              new DOMParser()
                .parseFromString(
                  html,
                  'text/html'
                );


            const priceHtml =
              getPriceHtmlFromDocument(
                doc
              );


            let badgeHtml =
              getBadgeHtmlFromDocument(
                doc
              );


            const onSale =
              priceIsOnSale(
                priceHtml
              );


            /*
              If Stretch's fetched product HTML
              does not contain its sale badge,
              build the same badge contents from
              the rendered price.
            */

            if (
              onSale &&
              !badgeHtml
            ) {

              const savings =
                getSavingsText(
                  priceHtml
                );


              if (savings) {

                badgeHtml =
                  `Save <span class="money">${savings}</span>`;

              }

            }


            return {

              url,

              priceHtml,

              badgeHtml,

              onSale

            };

          }
        )


        .catch(
          error => {

            CACHE.delete(
              url
            );


            console.warn(
              '[SV Collection Price + Badge]',
              error
            );


            return null;

          }
        );


    CACHE.set(
      url,
      request
    );


    return request;

  }



  


  /* ============================================================
     UPDATE SALE BADGE

     The original file called updateSaleBadge() but did not define it.
     Stretch/Maestrooo product cards use .product-card__badge-list
     with <on-sale-badge class="badge badge--on-sale">.
     ============================================================ */

  function updateSaleBadge(card, data) {
    if (!card || !data) return;

    const badgeList = card.querySelector(
      '.product-card__badge-list'
    );

    if (!badgeList) return;

    let saleBadge = badgeList.querySelector(
      'on-sale-badge'
    );

    if (!data.onSale) {
      saleBadge?.remove();
      return;
    }

    if (!data.badgeHtml) return;

    if (!saleBadge) {
      saleBadge = document.createElement(
        'on-sale-badge'
      );

      saleBadge.className =
        'badge badge--on-sale';

      badgeList.appendChild(saleBadge);
    }

    saleBadge.hidden = false;
    saleBadge.innerHTML = data.badgeHtml;
  }


  /* ============================================================
     UPDATE COLLECTION CARD
     ============================================================ */

  function applyVerifiedCardState(
    card,
    url,
    data
  ) {
    if (
      !card ||
      !data ||
      CURRENT_PRODUCT.get(card) !== url
    ) {
      return false;
    }

    const priceList =
      card.querySelector(
        '.product-card__info price-list:not(.au-market-price-lock)'
      );

    const priceLock =
      card.querySelector(
        '.au-market-price-lock'
      );

    if (
      data.priceHtml &&
      priceList
    ) {
      priceList.innerHTML =
        data.priceHtml;
    }

    if (
      data.priceHtml &&
      priceLock
    ) {
      priceLock.innerHTML =
        data.priceHtml;
    }

    updateSaleBadge(
      card,
      data
    );

    return true;
  }


  function stabilizeVerifiedCardState(
    card,
    url,
    data
  ) {
    const delays =
      [80, 260, 520, 820, 1200];

    delays.forEach(
      (delay, index) => {
        setTimeout(
          () => {
            if (
              !applyVerifiedCardState(
                card,
                url,
                data
              )
            ) {
              return;
            }

            if (
              index ===
              delays.length - 1
            ) {
              releasePriceLockAfterPaint(
                card
              );
            }
          },
          delay
        );
      }
    );
  }


  async function updateCard(
    card,
    productUrl
  ) {
    if (
      !card ||
      !productUrl
    ) {
      return;
    }

    const url =
      cleanUrl(
        productUrl
      );

    if (!url) return;

    if (
      CURRENT_PRODUCT.get(
        card
      ) === url
    ) {
      return;
    }

    beginPriceLock(card);

    CURRENT_PRODUCT.set(
      card,
      url
    );

    let data =
      await getProductData(
        url
      );

    if (
      !data &&
      CURRENT_PRODUCT.get(
        card
      ) === url
    ) {
      await new Promise(
        resolve =>
          setTimeout(
            resolve,
            180
          )
      );

      data =
        await getProductData(
          url
        );
    }

    if (!data) {
      if (
        CURRENT_PRODUCT.get(
          card
        ) === url
      ) {
        releasePriceLockAfterPaint(
          card
        );
      }

      return;
    }

    if (
      CURRENT_PRODUCT.get(
        card
      ) !== url
    ) {
      return;
    }

    applyVerifiedCardState(
      card,
      url,
      data
    );

    stabilizeVerifiedCardState(
      card,
      url,
      data
    );
  }


  /* ============================================================
     SV SELECTION
     ============================================================ */

  function handleSelection(event) {
    const swatch =
      event.target?.closest?.(
        SWATCH_SELECTOR
      );

    if (!swatch) return;

    const card =
      swatch.closest(
        'product-card'
      );

    if (!card) return;

    if (
      swatch.closest(
        'quick-buy-modal'
      )
    ) {
      return;
    }

    const productUrl =
      productUrlFromSwatch(
        swatch
      );

    if (!productUrl) return;

    updateCard(
      card,
      productUrl
    );
  }

  document.addEventListener(
    'pointerdown',
    handleSelection,
    true
  );

  document.addEventListener(
    'keydown',
    event => {
      if (
        event.key !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }

      handleSelection(event);
    },
    true
  );

  document.addEventListener(
    'click',
    event => {
      if (event.detail !== 0) {
        return;
      }

      handleSelection(event);
    },
    true
  );


})();


/* ============================================================
   AU NOIR — COLLECTION PERFORMANCE V8
   CONVERSION-SAFE STOCK FILTER + CLICK LOAD MORE

   Replaces:
   - automatic infinite scroll
   - global window.fetch override
   - global XMLHttpRequest override

   Keeps Shopify/Stretch in control of its own network layer.
   ============================================================ */
(() => {
  'use strict';

  const path = window.location.pathname;

  if (
    !path.includes('/collections/') ||
    path.includes('/products/')
  ) {
    return;
  }

  const STOCK_PARAM = 'filter.v.availability';
  const STOCK_VALUE = '1';

  const SECTION_SELECTOR =
    '.shopify-section--main-collection';

  const COLLECTION_SELECTOR =
    '#collection';

  const GRID_SELECTOR =
    '.product-list';

  const PAGER_SELECTOR =
    '.pagination';

  const LOAD_MORE_ID =
    'au-load-more-v3';

  let reinitTimer = 0;


  function sectionId(section) {
    if (
      !section ||
      !section.id ||
      !section.id.startsWith('shopify-section-')
    ) {
      return '';
    }

    return section.id.slice(
      'shopify-section-'.length
    );
  }


  function addStockParameter(value) {
    if (!value) return value;

    try {
      const originalValue = value;
      const url = new URL(
        value,
        window.location.origin
      );

      if (
        !url.pathname.includes('/collections/') ||
        url.pathname.includes('/products/')
      ) {
        return originalValue;
      }

      url.searchParams.set(
        STOCK_PARAM,
        STOCK_VALUE
      );

      if (
        typeof originalValue === 'string' &&
        !originalValue.startsWith('http://') &&
        !originalValue.startsWith('https://')
      ) {
        return (
          url.pathname +
          url.search +
          url.hash
        );
      }

      return url.toString();

    } catch (_) {
      return value;
    }
  }


  function ensureStockInput(form) {
    if (!form) return;

    let input = form.querySelector(
      'input[name="' + STOCK_PARAM + '"]'
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = STOCK_PARAM;
      form.appendChild(input);
    }

    input.value = STOCK_VALUE;
  }


  function enhanceNativeControls(root) {
    const scope = root || document;

    scope
      .querySelectorAll?.(
        'facets-form form, form.facets'
      )
      .forEach(ensureStockInput);

    scope
      .querySelectorAll?.(
        '#collection .pagination a[href], ' +
        '#collection facet-link a[href]'
      )
      .forEach(link => {
        link.setAttribute(
          'href',
          addStockParameter(
            link.getAttribute('href')
          )
        );
      });
  }


  function notifyProductApps(root) {
    document.dispatchEvent(
      new CustomEvent(
        'vsk:render:swatches',
        {
          bubbles: true,
          detail: { root: root || document }
        }
      )
    );

    document.dispatchEvent(
      new CustomEvent(
        'au:collection:products-added',
        {
          detail: { root: root || document }
        }
      )
    );
  }


  async function fetchCollectionDocument(
    requestedUrl,
    section
  ) {
    const url = new URL(
      requestedUrl,
      window.location.origin
    );

    url.searchParams.set(
      STOCK_PARAM,
      STOCK_VALUE
    );

    const id = sectionId(section);

    if (id) {
      url.searchParams.set(
        'section_id',
        id
      );
    }

    url.hash = '';

    let response = await fetch(
      url.toString(),
      {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With':
            'XMLHttpRequest'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Collection HTTP ' +
        response.status
      );
    }

    let documentResult =
      new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

    if (
      !documentResult.querySelector(
        COLLECTION_SELECTOR
      ) &&
      id
    ) {
      url.searchParams.delete('section_id');

      response = await fetch(
        url.toString(),
        {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          'Collection fallback HTTP ' +
          response.status
        );
      }

      documentResult =
        new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );
    }

    return documentResult;
  }


  async function applyInitialStockFilter() {
    const currentUrl =
      new URL(window.location.href);

    if (
      currentUrl.searchParams.get(
        STOCK_PARAM
      ) === STOCK_VALUE
    ) {
      return false;
    }

    const currentCollection =
      document.querySelector(
        COLLECTION_SELECTOR
      );

    const section =
      document.querySelector(
        SECTION_SELECTOR
      );

    if (
      !currentCollection ||
      !section
    ) {
      return false;
    }

    currentCollection.setAttribute(
      'aria-busy',
      'true'
    );

    try {
      const filteredDocument =
        await fetchCollectionDocument(
          currentUrl.toString(),
          section
        );

      const freshCollection =
        filteredDocument.querySelector(
          COLLECTION_SELECTOR
        );

      if (!freshCollection) {
        return false;
      }

      currentCollection.replaceWith(
        document.importNode(
          freshCollection,
          true
        )
      );

      enhanceNativeControls(document);
      notifyProductApps(
        document.querySelector(
          COLLECTION_SELECTOR
        )
      );

      return true;

    } catch (error) {
      console.error(
        '[AU NOIR Collection V8]',
        error
      );

      return false;

    } finally {
      document
        .querySelector(
          COLLECTION_SELECTOR
        )
        ?.removeAttribute(
          'aria-busy'
        );
    }
  }


  function installLoadMoreStyles() {
    if (
      document.getElementById(
        'au-load-more-v3-style'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'au-load-more-v3-style';

    style.textContent =
      '.au-load-more-v3{' +
      'display:flex;' +
      'flex-direction:column;' +
      'align-items:stretch;' +
      'gap:14px;' +
      'width:100%;' +
      'max-width:820px;' +
      'box-sizing:border-box;' +
      'margin:38px auto 12px;' +
      'padding:0 18px' +
      '}' +
      '.au-load-more-v3__count{' +
      'color:#fff;' +
      'font-size:16px;' +
      'font-weight:400;' +
      'line-height:1.35;' +
      'text-align:center;' +
      'opacity:1' +
      '}' +
      '.au-load-more-v3__progress{' +
      'position:relative;' +
      'width:100%;' +
      'height:4px;' +
      'overflow:hidden;' +
      'background:rgba(255,255,255,.26)' +
      '}' +
      '.au-load-more-v3__progress-bar{' +
      'display:block;' +
      'width:0%;' +
      'height:100%;' +
      'background:#fff;' +
      'transition:width .28s ease' +
      '}' +
      '.au-load-more-v3__button{' +
      'display:flex;' +
      'align-items:center;' +
      'justify-content:space-between;' +
      'width:100%;' +
      'min-height:58px;' +
      'padding:0 24px;' +
      'border:0;' +
      'border-radius:0;' +
      'background:#fff;' +
      'color:#000;' +
      'font:inherit;' +
      'font-size:15px;' +
      'font-weight:700;' +
      'letter-spacing:.02em;' +
      'text-transform:uppercase;' +
      'cursor:pointer;' +
      'transition:opacity .18s ease' +
      '}' +
      '.au-load-more-v3__button::after{' +
      'content:"›";' +
      'display:block;' +
      'font-size:32px;' +
      'font-weight:300;' +
      'line-height:1;' +
      'margin-left:18px' +
      '}' +
      '.au-load-more-v3__button:hover{' +
      'opacity:.88' +
      '}' +
      '.au-load-more-v3__button[aria-busy="true"],' +
      '.au-load-more-v3__button:disabled{' +
      'opacity:.55;' +
      'cursor:wait' +
      '}' +
      '@media(max-width:699px){' +
      '.au-load-more-v3{' +
      'gap:12px;' +
      'margin-top:30px;' +
      'padding:0 14px' +
      '}' +
      '.au-load-more-v3__count{' +
      'font-size:15px' +
      '}' +
      '.au-load-more-v3__button{' +
      'min-height:56px;' +
      'padding:0 20px;' +
      'font-size:14px' +
      '}' +
      '}';

    document.head.appendChild(style);
  }


  function nextPageUrl(pager) {
    if (!pager) return '';

    const direct =
      pager.querySelector(
        'a[rel="next"]'
      );

    if (direct?.href) {
      return addStockParameter(
        direct.href
      );
    }

    return '';
  }


  function updateLoadMoreCount(
    collection,
    grid,
    countElement
  ) {
    if (
      !collection ||
      !grid ||
      !countElement
    ) {
      return;
    }

    const loaded =
      grid.querySelectorAll(
        'product-card'
      ).length;

    const total =
      Number(
        collection.dataset
          .auProductCount || 0
      );

    const french =
      (
        document.documentElement.lang ||
        ''
      )
        .toLowerCase()
        .startsWith('fr');

    countElement.textContent =
      total > 0
        ? (
            french
              ? 'Vous avez consulté ' +
                loaded +
                ' produit(s) sur ' +
                total
              : 'You have viewed ' +
                loaded +
                ' product(s) out of ' +
                total
          )
        : String(loaded);

    const progressBar =
      countElement
        .parentElement
        ?.querySelector(
          '.au-load-more-v3__progress-bar'
        );

    if (progressBar) {
      const percentage =
        total > 0
          ? Math.min(
              100,
              Math.max(
                0,
                (loaded / total) * 100
              )
            )
          : 0;

      progressBar.style.width =
        percentage + '%';
    }
  }


  function setupLoadMore() {
    installLoadMoreStyles();

    const old =
      document.getElementById(
        LOAD_MORE_ID
      );

    old?.remove();

    const collection =
      document.querySelector(
        COLLECTION_SELECTOR
      );

    const grid =
      collection?.querySelector(
        GRID_SELECTOR
      );

    const pager =
      collection?.querySelector(
        PAGER_SELECTOR
      );

    if (
      !collection ||
      !grid ||
      !pager
    ) {
      return;
    }

    const initialNext =
      nextPageUrl(pager);

    if (!initialNext) {
      pager.hidden = false;
      return;
    }

    pager.hidden = true;

    const wrapper =
      document.createElement('div');

    wrapper.id =
      LOAD_MORE_ID;

    wrapper.className =
      'au-load-more-v3';

    const button =
      document.createElement('button');

    button.type = 'button';
    button.className =
      'au-load-more-v3__button';

    const french =
      (
        document.documentElement.lang ||
        ''
      )
        .toLowerCase()
        .startsWith('fr');

    button.textContent =
      french
        ? 'Plus de produits'
        : 'More products';

    const count =
      document.createElement('span');

    count.className =
      'au-load-more-v3__count';

    const progress =
      document.createElement('div');

    progress.className =
      'au-load-more-v3__progress';

    progress.setAttribute(
      'aria-hidden',
      'true'
    );

    const progressBar =
      document.createElement('span');

    progressBar.className =
      'au-load-more-v3__progress-bar';

    progress.appendChild(
      progressBar
    );

    wrapper.append(
      count,
      progress,
      button
    );

    pager.parentNode.insertBefore(
      wrapper,
      pager
    );

    updateLoadMoreCount(
      collection,
      grid,
      count
    );

    button.addEventListener(
      'click',
      async () => {
        const requested =
          nextPageUrl(pager);

        if (!requested) {
          wrapper.remove();
          pager.hidden = false;
          return;
        }

        button.disabled = true;
        button.setAttribute(
          'aria-busy',
          'true'
        );

        try {
          const section =
            document.querySelector(
              SECTION_SELECTOR
            );

          const freshDocument =
            await fetchCollectionDocument(
              requested,
              section
            );

          const freshGrid =
            freshDocument.querySelector(
              GRID_SELECTOR
            );

          const freshPager =
            freshDocument.querySelector(
              PAGER_SELECTOR
            );

          if (!freshGrid) {
            throw new Error(
              'Missing next product grid'
            );
          }

          const fragment =
            document.createDocumentFragment();

          Array
            .from(
              freshGrid.children
            )
            .forEach(node => {
              fragment.appendChild(
                document.importNode(
                  node,
                  true
                )
              );
            });

          if (
            !fragment.childNodes.length
          ) {
            wrapper.remove();
            pager.hidden = false;
            return;
          }

          grid.appendChild(fragment);

          if (freshPager) {
            pager.innerHTML =
              freshPager.innerHTML;

            pager.hidden = true;
          } else {
            pager.innerHTML = '';
          }

          enhanceNativeControls(
            collection
          );

          notifyProductApps(grid);

          updateLoadMoreCount(
            collection,
            grid,
            count
          );

          if (!nextPageUrl(pager)) {
            wrapper.remove();
            pager.hidden = true;
          }

        } catch (error) {
          console.error(
            '[AU NOIR Load More V3]',
            error
          );

          pager.hidden = false;
          wrapper.remove();

        } finally {
          button.disabled = false;
          button.removeAttribute(
            'aria-busy'
          );
        }
      }
    );
  }


  function scheduleReinit() {
    clearTimeout(reinitTimer);

    reinitTimer =
      setTimeout(
        () => {
          enhanceNativeControls(
            document
          );

          setupLoadMore();
        },
        80
      );
  }


  function observeNativeCollectionUpdates() {
    const section =
      document.querySelector(
        SECTION_SELECTOR
      );

    if (!section) return;

    const observer =
      new MutationObserver(
        mutations => {
          const meaningful =
            mutations.some(
              mutation =>
                Array
                  .from(
                    mutation.addedNodes
                  )
                  .some(node => {
                    if (
                      node.nodeType !== 1
                    ) {
                      return false;
                    }

                    return (
                      node.matches?.(
                        COLLECTION_SELECTOR +
                        ',' +
                        GRID_SELECTOR +
                        ',' +
                        PAGER_SELECTOR
                      ) ||
                      node.querySelector?.(
                        COLLECTION_SELECTOR +
                        ',' +
                        GRID_SELECTOR +
                        ',' +
                        PAGER_SELECTOR
                      )
                    );
                  })
            );

          if (meaningful) {
            scheduleReinit();
          }
        }
      );

    observer.observe(
      section,
      {
        childList: true,
        subtree: true
      }
    );
  }


  document.addEventListener(
    'change',
    event => {
      const form =
        event.target?.closest?.(
          'facets-form form, form.facets'
        );

      if (form) {
        ensureStockInput(form);
      }
    },
    true
  );


  document.addEventListener(
    'submit',
    event => {
      const form =
        event.target?.closest?.(
          'facets-form form, form.facets'
        );

      if (form) {
        ensureStockInput(form);
      }
    },
    true
  );


  document.addEventListener(
    'click',
    event => {
      const link =
        event.target?.closest?.(
          '#collection .pagination a[href], ' +
          '#collection facet-link a[href]'
        );

      if (!link) return;

      link.setAttribute(
        'href',
        addStockParameter(
          link.getAttribute('href')
        )
      );
    },
    true
  );


  async function start() {
    enhanceNativeControls(document);

    await applyInitialStockFilter();

    enhanceNativeControls(document);
    setupLoadMore();
    observeNativeCollectionUpdates();
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }


  window.addEventListener(
    'pageshow',
    event => {
      if (event.persisted) {
        scheduleReinit();
      }
    }
  );

})();


/* ============================================================
   AU NOIR — QUICK VIEW COLOR HANDOFF V7
   NATIVE STRETCH RELOAD, PDP FALLBACK

   No product-page fetch/parsing.
   No gallery DOM replacement.
   No pointerdown interception.
   No prefetch cache.
   ============================================================ */
(() => {
  'use strict';

  const SWATCH_SELECTOR = [
    '.swatch-group-selector',
    '.star-set-image',
    '.swatch-view-item'
  ].join(',');


  function normalizeProductUrl(value) {
    if (
      !value ||
      typeof value !== 'string'
    ) {
      return '';
    }

    const rootPath = (() => {
      try {
        return new URL(
          window.Shopify?.routes?.root || '/',
          window.location.origin
        ).pathname.replace(/\/+$/, '');
      } catch (_) {
        return '';
      }
    })();

    try {
      const url =
        new URL(
          value,
          window.location.origin
        );

      const productIndex =
        url.pathname.indexOf(
          '/products/'
        );

      if (productIndex === -1) {
        return '';
      }

      url.pathname =
        rootPath +
        url.pathname.slice(
          productIndex
        );

      return (
        url.pathname +
        url.search +
        url.hash
      );

    } catch (_) {
      return '';
    }
  }

  function directSwatchUrl(swatch) {
    if (!swatch) return '';

    const candidates = [
      swatch.getAttribute?.(
        'swatch-url'
      ),
      swatch.getAttribute?.(
        'data-product-url'
      ),
      swatch.getAttribute?.(
        'data-url'
      ),
      swatch.getAttribute?.(
        'href'
      )
    ];

    for (
      const candidate of candidates
    ) {
      const url =
        normalizeProductUrl(
          candidate || ''
        );

      if (url) return url;
    }

    const handle =
      swatch.getAttribute?.(
        'data-product-handle'
      ) ||
      swatch.getAttribute?.(
        'data-handle'
      );

    if (
      handle &&
      /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(
        handle
      )
    ) {
      return normalizeProductUrl(
        '/products/' +
        handle
      );
    }

    const nested =
      swatch.querySelector?.(
        '[swatch-url], ' +
        '[data-product-url], ' +
        '[data-url], ' +
        'a[href*="/products/"]'
      );

    if (
      nested &&
      nested !== swatch
    ) {
      return directSwatchUrl(
        nested
      );
    }

    return '';
  }


  function sameProduct(a, b) {
    const first =
      normalizeProductUrl(a);

    const second =
      normalizeProductUrl(b);

    if (!first || !second) {
      return false;
    }

    try {
      return (
        new URL(
          first,
          window.location.origin
        ).pathname ===
        new URL(
          second,
          window.location.origin
        ).pathname
      );
    } catch (_) {
      return false;
    }
  }


  function nativeReloadOrNavigate(
    modal,
    targetUrl
  ) {
    const normalized =
      normalizeProductUrl(
        targetUrl
      );

    if (!normalized) return;

    const trigger =
      modal.id
        ? document.querySelector(
            '[aria-controls="' +
            CSS.escape(modal.id) +
            '"]'
          )
        : null;

    if (
      !trigger ||
      typeof modal.hide !==
        'function'
    ) {
      window.location.assign(
        normalized
      );

      return;
    }

    let completed = false;


    function reopen() {
      if (completed) return;

      completed = true;

      modal.setAttribute(
        'product-url',
        normalized
      );

      modal.dataset.auVisibleProductUrl =
        normalized;

      modal.removeAttribute(
        'loaded'
      );

      delete modal.dataset.loaded;

      try {
        if ('loaded' in modal) {
          modal.loaded = false;
        }
      } catch (_) {}

      modal.replaceChildren();

      requestAnimationFrame(
        () => {
          trigger.click();
        }
      );
    }


    modal.addEventListener(
      'dialog:after-hide',
      reopen,
      { once: true }
    );

    try {
      modal.hide();
    } catch (_) {
      window.location.assign(
        normalized
      );

      return;
    }


    setTimeout(
      () => {
        if (completed) return;

        if (
          !modal.hasAttribute(
            'open'
          )
        ) {
          reopen();
          return;
        }

        window.location.assign(
          normalized
        );
      },
      450
    );
  }


  document.addEventListener(
    'dialog:after-show',
    event => {
      const modal =
        event.target?.matches?.(
          'quick-buy-modal'
        )
          ? event.target
          : null;

      if (!modal) return;

      const current =
        normalizeProductUrl(
          modal.getAttribute(
            'product-url'
          ) || ''
        );

      if (current) {
        modal.dataset.auVisibleProductUrl =
          current;
      }
    }
  );


  document.addEventListener(
    'click',
    event => {
      const swatch =
        event.target?.closest?.(
          SWATCH_SELECTOR
        );

      if (!swatch) return;

      const modal =
        swatch.closest(
          'quick-buy-modal[open]'
        );

      if (!modal) return;

      const targetUrl =
        directSwatchUrl(
          swatch
        );

      if (!targetUrl) {
        return;
      }

      const currentUrl =
        modal.dataset
          .auVisibleProductUrl ||
        modal.getAttribute(
          'product-url'
        ) ||
        '';

      if (
        sameProduct(
          targetUrl,
          currentUrl
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      nativeReloadOrNavigate(
        modal,
        targetUrl
      );
    },
    true
  );

})();


/* ==========================================================
   PROGUS STORE LOCATOR - MOBILE GEOLOCATION FIX
   PERFORMANCE OPTIMIZED
   ========================================================== */

(function () {
  'use strict';

  const FRAME_SELECTOR =
    'iframe[src*="proguscommerce.com"], iframe[src*="progus.com"]';

  function fixFrame(frame) {
    if (!frame || !frame.matches?.(FRAME_SELECTOR)) return;

    const currentAllow = frame.getAttribute('allow') || '';

    if (!currentAllow.toLowerCase().includes('geolocation')) {
      frame.setAttribute(
        'allow',
        currentAllow
          ? currentAllow + '; geolocation'
          : 'geolocation'
      );

      if (!frame.dataset.progusGeoReloaded) {
        frame.dataset.progusGeoReloaded = 'true';

        const src = frame.getAttribute('src');
        if (src) frame.setAttribute('src', src);
      }
    }
  }

  function scan(root) {
    if (!root) return;

    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.(FRAME_SELECTOR)) {
      fixFrame(root);
    }

    root.querySelectorAll?.(FRAME_SELECTOR).forEach(fixFrame);
  }

  scan(document);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          scan(node);
        }
      });
    }
  });

  const root = document.querySelector('main') || document.documentElement;
  if (root) {
    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('load', () => scan(document), { once: true });
})();

/* SA Variants - high quality + garment/right-side crop — OPTIMIZED */
(function () {
  'use strict';

  function improveSwatch(swatch) {
    if (!swatch || swatch.dataset.hqSwatch === 'true') return;

    const bg = swatch.style.backgroundImage;
    if (!bg) return;

    const match = bg.match(/url\(["']?(.*?)["']?\)/);
    if (!match || !match[1]) return;

    try {
      const url = new URL(match[1], window.location.origin);

      url.searchParams.set('width', '200');
      url.searchParams.set('height', '200');
      url.searchParams.set('crop', 'right');

      swatch.style.backgroundImage = 'url("' + url.toString() + '")';
      swatch.dataset.hqSwatch = 'true';
    } catch (_) {}
  }

  function improveSASwatches(root = document) {
    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.('.star-set-image')) {
      improveSwatch(root);
    }

    root.querySelectorAll?.('.star-set-image').forEach(improveSwatch);
  }

  improveSASwatches(document);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          improveSASwatches(node);
        }
      });
    }
  });

  const root = document.querySelector('main') || document.body;
  if (root) {
    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }
})();

/* ============================================================
   AU NOIR
   SYNC QUICK SIZE ADD WITH SV / STARAPPS LINKED PRODUCT SWATCHES
   PERFORMANCE OPTIMIZED

   IMPORTANT:
   - Does NOT fetch every collection product on page load.
   - Does NOT watch the full document for URL mutations.
   - Fetches only after a real customer changes a linked color.
   - Product JSON is cached, so repeated cards/colors reuse the request.
   ============================================================ */

(() => {
  'use strict';

  if (window.AUNoirQuickSizeSyncInstalled) return;
  window.AUNoirQuickSizeSyncInstalled = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  const PRODUCT_CACHE = new Map();

  const MARKET_ROOT_PATH = (() => {
    try {
      return new URL(ROOT, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (_) {
      return '';
    }
  })();

  const makeMarketAwareProductUrl = value => {
    if (!value || typeof value !== 'string') return value;

    try {
      const parsed = new URL(value, window.location.origin);
      const productIndex = parsed.pathname.indexOf('/products/');

      if (productIndex === -1) return value;

      parsed.pathname =
        (MARKET_ROOT_PATH || '') +
        parsed.pathname.slice(productIndex);

      return parsed.pathname + parsed.search + parsed.hash;
    } catch (_) {
      return value;
    }
  };

  const normalizeSASwatchUrls = root => {
    const scope = root || document;
    const candidates = [];

    if (
      scope.nodeType === 1 &&
      scope.matches?.(
        '.swatch-group-selector, .star-set-image, .swatch-view-item'
      )
    ) {
      candidates.push(scope);
    }

    scope
      .querySelectorAll?.(
        '.swatch-group-selector, .star-set-image, .swatch-view-item'
      )
      .forEach(node => candidates.push(node));

    candidates.forEach(swatch => {
      const nodes = [
        swatch,
        ...swatch.querySelectorAll(
          '[swatch-url], [data-product-url], [data-url], a[href*="/products/"]'
        )
      ];

      nodes.forEach(node => {
        ['swatch-url','data-product-url','data-url','href'].forEach(attribute => {
          const value = node.getAttribute?.(attribute);

          if (!value || !value.includes('/products/')) return;

          const localized = makeMarketAwareProductUrl(value);

          if (localized && localized !== value) {
            node.setAttribute(attribute, localized);
          }
        });
      });
    });
  };

  const getHandleFromUrl = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  };


  const getCurrentHandle = (card) => {
    const titleLink = card.querySelector(
      '.product-title[href*="/products/"]'
    );

    if (titleLink) {
      const handle = getHandleFromUrl(titleLink.href);
      if (handle) return handle;
    }

    const imageLink = card.querySelector(
      '.product-card__media[href*="/products/"]'
    );

    if (imageLink) {
      const handle = getHandleFromUrl(imageLink.href);
      if (handle) return handle;
    }

    return card.getAttribute('handle');
  };


  const getSizeOption = (product) => {
    if (!Array.isArray(product.options)) return null;

    let sizeOption = product.options.find(option => {
      const name = typeof option === 'string'
        ? option
        : option.name;

      if (!name) return false;

      const cleanName = name.toLowerCase().trim();

      return (
        cleanName === 'size' ||
        cleanName === 'taille' ||
        cleanName === 'grandeur' ||
        cleanName.includes('size') ||
        cleanName.includes('taille')
      );
    });

    if (!sizeOption) {
      const candidates = product.options
        .map((option, index) => {
          if (typeof option === 'string') {
            const values = [
              ...new Set(
                product.variants.map(
                  variant => variant.options?.[index]
                )
              )
            ].filter(Boolean);

            return {
              name: option,
              position: index + 1,
              values
            };
          }

          return option;
        })
        .sort(
          (a, b) =>
            (b.values?.length || 0) -
            (a.values?.length || 0)
        );

      sizeOption = candidates[0];
    }

    if (!sizeOption) return null;

    if (typeof sizeOption === 'string') {
      return {
        name: sizeOption,
        position: product.options.indexOf(sizeOption) + 1
      };
    }

    return {
      name: sizeOption.name,
      position: sizeOption.position
    };
  };


  function fetchProductJSON(handle) {
    if (!handle) return Promise.resolve(null);

    const cacheKey =
      ROOT + '|' + handle;

    if (
      PRODUCT_CACHE.has(
        cacheKey
      )
    ) {
      return PRODUCT_CACHE.get(
        cacheKey
      );
    }

    const request = fetch(
      `${ROOT}products/${encodeURIComponent(handle)}.js`,
      { credentials: 'same-origin' }
    )
      .then(response => {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(error => {
        PRODUCT_CACHE.delete(
          cacheKey
        );
        console.error(
          'AU NOIR Quick Size fetch error:',
          error
        );
        return null;
      });

    PRODUCT_CACHE.set(
      cacheKey,
      request
    );

    return request;
  }


  const updateQuickSizes = async (card, forcedHandle = null) => {
    if (!card) return;

    const sizeContainer =
      card.querySelector('.boss-quick-size__sizes') ||
      card.querySelector('.boss-quick-shop__sizes');

    if (!sizeContainer) return;

    const handle = forcedHandle || getCurrentHandle(card);
    if (!handle) return;

    try {
      const product = await fetchProductJSON(handle);
      if (!product) return;

      if (sizeContainer.dataset.productHandle === handle) {
        return;
      }

      // The card may have been switched again while the request was running.
      const selectedFromCard = card.dataset.auQuickSizeRequestedHandle;
      if (selectedFromCard && selectedFromCard !== handle) {
        return;
      }

      const sizeOption = getSizeOption(product);
      if (!sizeOption) return;

      const sizeIndex = Number(sizeOption.position) - 1;

      const form =
        sizeContainer.matches?.('form')
          ? sizeContainer
          : sizeContainer.closest?.('form') ||
            sizeContainer.querySelector?.('form');

      if (!form) {
        return;
      }

      const idInput =
        form.querySelector(
          'input[name="id"]'
        );

      if (!idInput) {
        return;
      }

      const productIdInput =
        form.querySelector(
          'input[name="product-id"]'
        );

      if (
        productIdInput &&
        product.id
      ) {
        productIdInput.value =
          product.id;
      }

      const buttonTemplate =
        sizeContainer.querySelector(
          '.boss-quick-size__button, ' +
          '.boss-quick-shop__size, ' +
          'button[type="submit"]'
        );

      if (!buttonTemplate) {
        return;
      }

      const fragment =
        document.createDocumentFragment();

      const usedSizes =
        new Set();

      let firstAvailableId = '';

      product.variants.forEach(
        variant => {
          const size =
            variant.options?.[sizeIndex] ||
            variant[
              'option' +
              sizeOption.position
            ];

          if (
            !size ||
            usedSizes.has(size)
          ) {
            return;
          }

          usedSizes.add(size);

          const button =
            buttonTemplate.cloneNode(
              true
            );

          button.dataset.auVariantId =
            String(variant.id);

          const buttonContent =
            button.querySelector(
              '.button__content'
            );

          if (buttonContent) {
            buttonContent.textContent =
              size;

            buttonContent.removeAttribute(
              'rotated-text'
            );

            buttonContent.removeAttribute(
              'up-text'
            );

            buttonContent.removeAttribute(
              'glowing'
            );
          } else {
            button.textContent =
              size;
          }

          button.disabled =
            !variant.available;

          button.setAttribute(
            'aria-disabled',
            variant.available
              ? 'false'
              : 'true'
          );

          button.classList.toggle(
            'is-disabled',
            !variant.available
          );

          if (
            variant.available &&
            !firstAvailableId
          ) {
            firstAvailableId =
              String(variant.id);
          }

          fragment.appendChild(
            button
          );
        }
      );

      sizeContainer
        .querySelectorAll(
          '.boss-quick-size__button, ' +
          '.boss-quick-shop__size'
        )
        .forEach(
          button => button.remove()
        );

      sizeContainer.appendChild(
        fragment
      );

      if (firstAvailableId) {
        idInput.value =
          firstAvailableId;
      }

      sizeContainer.dataset.productHandle =
        handle;

      card.dataset.quickSizeHandle =
        handle;

    } catch (error) {
      console.error('AU NOIR Quick Size sync error:', error);
    }
  };


  const initialNormalize =
    () => normalizeSASwatchUrls(document);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialNormalize,
      { once: true }
    );
  } else {
    initialNormalize();
  }

  document.addEventListener(
    'au:collection:products-added',
    event => {
      normalizeSASwatchUrls(
        event.detail?.root || document
      );
    }
  );

  document.addEventListener(
    'pointerdown',
    event => {
      const swatch = event.target?.closest?.(
        '.swatch-group-selector, .star-set-image, .swatch-view-item'
      );

      if (!swatch) return;

      normalizeSASwatchUrls(swatch);
    },
    true
  );

  document.addEventListener(
    'click',
    event => {
      if (!event.isTrusted) return;

      /* Quick View is handled separately. Never let collection-card
         Quick Size synchronization react to swatches inside the modal. */
      if (event.target.closest?.('quick-buy-modal')) return;

      const card = event.target.closest?.('product-card');
      if (!card) return;

      // Prefer the actual linked-product element because it gives us
      // the new product immediately; no need to watch href mutations.
      const linkedSwatch = event.target.closest?.(
        '.swatch-group-selector[swatch-url]'
      );

      const anySASwatch = linkedSwatch || event.target.closest?.(
        '.star-set-image, .swatch-view-item'
      );

      if (!anySASwatch) return;

      const directHandle = getHandleFromUrl(
        linkedSwatch?.getAttribute('swatch-url') || ''
      );

      if (directHandle) {
        card.dataset.auQuickSizeRequestedHandle = directHandle;

        // First pass can update immediately while the app changes images/title.
        updateQuickSizes(card, directHandle);

        setTimeout(
          () => updateQuickSizes(card, directHandle),
          260
        );

        setTimeout(
          () => updateQuickSizes(card, directHandle),
          750
        );

        return;
      }

      // Fallback for StarApps markup that does not expose swatch-url
      // on the clicked element. This runs only after a real click.
      setTimeout(() => {
        const handle = getCurrentHandle(card);
        if (!handle) return;
        card.dataset.auQuickSizeRequestedHandle = handle;
        updateQuickSizes(card, handle);
      }, 180);
    },
    true
  );

  document.addEventListener(
    'click',
    event => {
      const button =
        event.target?.closest?.(
          '.boss-quick-size__button[data-au-variant-id], ' +
          '.boss-quick-shop__size[data-au-variant-id]'
        );

      if (!button) return;

      const form =
        button.closest('form');

      const idInput =
        form?.querySelector(
          'input[name="id"]'
        );

      if (!idInput) return;

      idInput.value =
        button.dataset.auVariantId;
    },
    true
  );


})();

/* ============================================================
   AU NOIR — REMOVE STRAY COLON BEFORE PRICE

   Handles the initial product page plus dynamically inserted /
   replaced prices from Stretch, Quick Buy, SV Variants, filters,
   infinite scroll, and AJAX updates.

   Example fixed:
   <span class="sr-only">Selling price</span>: €94.95
   becomes:
   <span class="sr-only">Selling price</span>€94.95

   IMPORTANT:
   - Only removes a leading colon in the text node immediately after
     the sr-only accessibility label.
   - Does NOT edit currency symbols or price digits.
   ============================================================ */

(() => {
  'use strict';

  const PRICE_SELECTOR =
    'sale-price, compare-at-price';


  function cleanPriceElement(
    priceElement
  ) {

    if (!priceElement) {
      return;
    }


    const srOnly =
      priceElement.querySelector(
        ':scope > .sr-only'
      );


    if (!srOnly) {
      return;
    }


    let node =
      srOnly.nextSibling;


    while (
      node &&
      node.nodeType === Node.TEXT_NODE &&
      node.textContent.trim() === ''
    ) {
      node = node.nextSibling;
    }


    if (
      !node ||
      node.nodeType !== Node.TEXT_NODE
    ) {
      return;
    }


    const current =
      node.textContent;


    const cleaned =
      current.replace(
        /^\s*:\s*/,
        ''
      );


    if (cleaned !== current) {
      node.textContent = cleaned;
    }

  }


  function cleanPrices(
    root = document
  ) {

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      root.matches?.(PRICE_SELECTOR)
    ) {
      cleanPriceElement(root);
    }


    root
      .querySelectorAll?.(PRICE_SELECTOR)
      .forEach(cleanPriceElement);

  }


  function initialClean() {
    cleanPrices(document);
  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialClean,
      { once: true }
    );

  } else {

    initialClean();

  }


  const observer =
    new MutationObserver(
      mutations => {

        for (const mutation of mutations) {

          if (
            mutation.type === 'characterData'
          ) {

            const priceElement =
              mutation.target.parentElement
                ?.closest?.(PRICE_SELECTOR);


            if (priceElement) {
              cleanPriceElement(
                priceElement
              );
            }


            continue;
          }


          if (
            mutation.type === 'childList'
          ) {

            mutation.addedNodes.forEach(
              node => {

                if (
                  node.nodeType ===
                  Node.ELEMENT_NODE
                ) {

                  cleanPrices(node);

                }

              }
            );


            const targetPrice =
              mutation.target.nodeType ===
              Node.ELEMENT_NODE
                ? mutation.target.closest?.(
                    PRICE_SELECTOR
                  )
                : mutation.target.parentElement
                    ?.closest?.(
                      PRICE_SELECTOR
                    );


            if (targetPrice) {
              cleanPriceElement(
                targetPrice
              );
            }

          }

        }

      }
    );


  const startObserver = () => {

    if (!document.documentElement) {
      return;
    }


    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
        characterData: true
      }
    );

  };


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );

  } else {

    startObserver();

  }

})();

/* ============================================================
   AU NOIR — FIRST COLLECTION PRODUCT MOBILE SWIPE HINT
   SMOOTH / AFTER FULL PAGE LOAD

   - Mobile only
   - Collection pages only
   - First collection product only
   - Waits until page + first 2 images are fully loaded
   ============================================================ */

(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';

  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  if (
    !window.location.pathname.includes('/collections/') ||
    window.location.pathname.includes('/products/')
  ) {
    return;
  }

  if (window.auNoirFirstProductSwipeHint) return;
  window.auNoirFirstProductSwipeHint = true;


  /* ============================================================
     SMOOTH SCROLL ANIMATION
     ============================================================ */

  function animateScroll(
    gallery,
    from,
    to,
    duration,
    callback
  ) {

    const startTime = performance.now();

    function ease(t) {

      /*
        Smooth ease-in-out
      */

      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function frame(now) {

      const progress = Math.min(
        (now - startTime) / duration,
        1
      );

      const eased = ease(progress);

      gallery.scrollLeft =
        from +
        ((to - from) * eased);

      if (progress < 1) {

        requestAnimationFrame(frame);

      } else {

        gallery.scrollLeft = to;

        if (callback) callback();

      }

    }

    requestAnimationFrame(frame);
  }


  /* ============================================================
     WAIT FOR IMAGE
     ============================================================ */

  async function waitForImage(img) {

    if (!img) return;

    /*
      Force first product's second image to load.
      Only affects this one product.
    */

    img.loading = 'eager';

    if (!img.complete) {

      await new Promise(resolve => {

        const finish = () => resolve();

        img.addEventListener(
          'load',
          finish,
          { once: true }
        );

        img.addEventListener(
          'error',
          finish,
          { once: true }
        );

      });

    }

    /*
      Wait until browser has actually decoded
      the image before starting animation.
    */

    if (typeof img.decode === 'function') {

      try {

        await img.decode();

      } catch (_) {}

    }

  }


  /* ============================================================
     RUN SWIPE HINT
     ============================================================ */

  async function runHint(gallery) {

    if (!gallery) return;

    if (
      gallery.dataset.auSwipeHintDone === 'true'
    ) {
      return;
    }

    const images =
      gallery.querySelectorAll(
        '.product-card__gallery-image'
      );

    if (images.length < 2) return;


    /* ----------------------------------------------------------
       WAIT FOR IMAGE #1 + IMAGE #2
       ---------------------------------------------------------- */

    await Promise.all([
      waitForImage(images[0]),
      waitForImage(images[1])
    ]);


    /*
      Give browser two complete paint cycles
      after decoding.
    */

    await new Promise(resolve => {

      requestAnimationFrame(() => {

        requestAnimationFrame(resolve);

      });

    });


    if (
      gallery.clientWidth < 50 ||
      gallery.scrollWidth <=
        gallery.clientWidth + 20
    ) {
      return;
    }


    gallery.dataset.auSwipeHintDone = 'true';


    /* ==========================================================
       TEMPORARILY DISABLE SNAP
       ========================================================== */

    const oldSnap =
      gallery.style.getPropertyValue(
        'scroll-snap-type'
      );

    const oldSnapPriority =
      gallery.style.getPropertyPriority(
        'scroll-snap-type'
      );

    const oldBehavior =
      gallery.style.getPropertyValue(
        'scroll-behavior'
      );

    const oldBehaviorPriority =
      gallery.style.getPropertyPriority(
        'scroll-behavior'
      );


    gallery.style.setProperty(
      'scroll-snap-type',
      'none',
      'important'
    );

    gallery.style.setProperty(
      'scroll-behavior',
      'auto',
      'important'
    );


    gallery.scrollLeft = 0;


    /* ==========================================================
       DISTANCE
       ========================================================== */

    const distance = Math.min(
      75,
      Math.max(
        55,
        gallery.clientWidth * 0.18
      )
    );


    /*
      Small delay after everything is completely ready.
    */

    await new Promise(
      resolve =>
        setTimeout(resolve, 500)
    );


    /* ==========================================================
       MOVE TOWARD IMAGE 2
       ========================================================== */

    animateScroll(
      gallery,
      0,
      distance,
      500,

      () => {

        /*
          Let customer see the second photo slightly.
        */

        setTimeout(() => {


          /* ====================================================
             RETURN TO IMAGE 1
             ==================================================== */

          animateScroll(
            gallery,
            distance,
            0,
            600,

            () => {

              gallery.scrollLeft = 0;


              /* ================================================
                 RESTORE NORMAL CAROUSEL
                 ================================================ */

              if (oldSnap) {

                gallery.style.setProperty(
                  'scroll-snap-type',
                  oldSnap,
                  oldSnapPriority
                );

              } else {

                gallery.style.removeProperty(
                  'scroll-snap-type'
                );

              }


              if (oldBehavior) {

                gallery.style.setProperty(
                  'scroll-behavior',
                  oldBehavior,
                  oldBehaviorPriority
                );

              } else {

                gallery.style.removeProperty(
                  'scroll-behavior'
                );

              }

            }
          );

        }, 250);

      }
    );

  }


  /* ============================================================
     FIND FIRST REAL COLLECTION PRODUCT
     ============================================================ */

  function initialize() {

    const productList =
      document.querySelector(
        'main .product-list'
      ) ||
      document.querySelector(
        '.product-list'
      );

    if (!productList) return;


    const firstCard =
      productList.querySelector(
        'product-card'
      );

    if (!firstCard) return;


    const gallery =
      firstCard.querySelector(
        '.product-card__media--gallery'
      );

    if (!gallery) return;


    /* ==========================================================
       WAIT UNTIL PRODUCT IS ACTUALLY VISIBLE
       ========================================================== */

    const observer =
      new IntersectionObserver(
        entries => {

          const entry = entries[0];

          if (!entry.isIntersecting) return;

          observer.disconnect();

          runHint(gallery);

        },
        {
          threshold: 0.4
        }
      );


    observer.observe(gallery);

  }


  /* ============================================================
     WAIT UNTIL EVERYTHING ON PAGE IS LOADED
     ============================================================ */

  function startAfterFullLoad() {

    /*
      Extra delay gives Shopify / apps /
      swatches / layout time to settle.
    */

    setTimeout(() => {

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          initialize();

        });

      });

    }, 700);

  }


  if (document.readyState === 'complete') {

    startAfterFullLoad();

  } else {

    window.addEventListener(
      'load',
      startAfterFullLoad,
      { once: true }
    );

  }

})();

/* =========================================================
   AU NOIR — MOBILE FILTER GLASS BUTTON
   PERFORMANCE OPTIMIZED / SINGLE COPY
   ========================================================= */

(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';
  const path = window.location.pathname;

  if (!window.matchMedia(MOBILE_QUERY).matches) return;
  if (!path.includes('/collections/') || path.includes('/products/')) return;


  function considerElement(el) {
    if (!el || !el.matches?.('button, a, [role="button"], summary')) {
      return;
    }

    if (el.classList.contains('au-mobile-filter-glass')) return;

    const text = (el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const isFilterButton =
      text.includes('filter and sort') ||
      text.includes('filtrer et trier');

    if (!isFilterButton) return;

    // Only now do a layout read. We no longer measure every button/link.
    const rect = el.getBoundingClientRect();

    if (rect.width > 180 && rect.height < 100) {
      el.classList.add('au-mobile-filter-glass');
      el.parentElement?.classList.add(
        'au-mobile-filter-glass-wrapper'
      );
    }
  }


  function styleWithin(root) {
    if (!root) return;

    if (root.nodeType === Node.ELEMENT_NODE) {
      considerElement(root);
    }

    root.querySelectorAll?.(
      'button, a, [role="button"], summary'
    ).forEach(considerElement);
  }


  function initialStyle() {
    styleWithin(document.querySelector('main') || document);
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialStyle,
      { once: true }
    );
  } else {
    initialStyle();
  }


  const startObserver = () => {
    const root = document.querySelector('main');
    if (!root) return;

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            styleWithin(node);
          }
        });
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true
    });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }

})();

/* =========================================================
   AU NOIR — GLASS DRAWERS + QUICK VIEW
   Stretch Theme / Shadow DOM
   Mobile + Desktop — PERFORMANCE OPTIMIZED
   ========================================================= */

(function () {
  'use strict';

  const SHADOW_STYLE_ID = 'au-noir-glass-drawer-shadow';

  const DRAWER_SELECTOR = [
    'x-drawer',
    'cart-drawer',
    'search-drawer',
    'facet-drawer',
    'facets-drawer',
    'header-drawer',
    'quick-buy-modal',
    'quick-view-modal',
    '[id*="quick-buy"]',
    '[id*="quick-view"]',
    '[class*="quick-buy-modal"]',
    '[class*="quick-view-modal"]',
    '[id*="drawer"]',
    '[class~="drawer"]'
  ].join(',');

  const shadowCSS = `
    :host {
      --au-glass-bg: rgba(18, 18, 20, 0.74);
    }

    header,
    footer,
    .drawer__header,
    .drawer__footer,
    .dialog__header,
    .dialog__footer,
    .modal__header,
    .modal__footer,
    [part="header"],
    [part="footer"],
    [class*="drawer-header"],
    [class*="drawer-footer"],
    [class*="drawer__header"],
    [class*="drawer__footer"],
    [class*="modal-header"],
    [class*="modal-footer"],
    [class*="modal__header"],
    [class*="modal__footer"] {
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
    }

    header,
    .drawer__header,
    .dialog__header,
    .modal__header,
    [part="header"] {
      border-bottom: 1px solid rgba(255,255,255,0.09) !important;
    }

    footer,
    .drawer__footer,
    .dialog__footer,
    .modal__footer,
    [part="footer"] {
      border-top: 1px solid rgba(255,255,255,0.09) !important;
    }

    [part="content"],
    .drawer__content,
    .dialog__content,
    .modal__content,
    .quick-buy-modal__content,
    .quick-view-modal__content {
      background: rgba(18,18,20,0.74) !important;
      background-color: rgba(18,18,20,0.74) !important;
      -webkit-backdrop-filter: blur(28px) saturate(180%) !important;
      backdrop-filter: blur(28px) saturate(180%) !important;
    }

    .quick-buy-modal__inner,
    .quick-buy-modal__content,
    .quick-buy-modal__product,
    .quick-view-modal__inner,
    .quick-view-modal__content,
    .quick-view-modal__product,
    .product-info,
    .product-info__block-list,
    .product-info__block-item,
    .product-info__buy-buttons,
    .product,
    .product__info,
    .product__info-container {
      background: transparent !important;
      background-color: transparent !important;
    }

    .product-gallery,
    .product-gallery__media,
    .product-gallery__media-list,
    .product-gallery__carousel,
    .product-media-container {
      background: transparent !important;
      background-color: transparent !important;
    }

    button[aria-label*="Close"],
    button[aria-label*="close"],
    button[aria-label*="Fermer"],
    button[aria-label*="fermer"],
    .drawer__close,
    .dialog__close,
    .modal__close,
    [class*="close-button"] {
      background: rgba(255,255,255,0.08) !important;
      border: 1px solid rgba(255,255,255,0.10) !important;
      border-radius: 999px !important;
      -webkit-backdrop-filter: blur(14px) !important;
      backdrop-filter: blur(14px) !important;
    }
  `;


  function injectIntoShadowRoot(element) {
    if (!element?.shadowRoot) return false;

    if (element.shadowRoot.querySelector('#' + SHADOW_STYLE_ID)) {
      return true;
    }

    const style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = shadowCSS;
    element.shadowRoot.appendChild(style);
    return true;
  }


  function scanRoot(root) {
    if (!root) return false;

    let found = false;

    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.(DRAWER_SELECTOR)) {
      found = true;
      injectIntoShadowRoot(root);
    }

    const descendants = root.querySelectorAll?.(DRAWER_SELECTOR);

    if (descendants?.length) {
      found = true;
      descendants.forEach(injectIntoShadowRoot);
    }

    return found;
  }


  function retryTarget(root) {
    // Do not create retry timers for ordinary product/image/text insertions.
    // Retry only when the inserted subtree actually contains a drawer/modal.
    const found = scanRoot(root);
    if (!found) return;

    // Custom elements may attach their shadowRoot just after insertion.
    setTimeout(() => scanRoot(root), 60);
    setTimeout(() => scanRoot(root), 250);
  }


  function initialScan() {
    scanRoot(document);
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialScan,
      { once: true }
    );
  } else {
    initialScan();
  }


  // One small post-load pass for Stretch components upgraded late.
  window.addEventListener(
    'load',
    () => setTimeout(initialScan, 250),
    { once: true }
  );


  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          retryTarget(node);
        }
      });
    }
  });


  const startObserver = () => {
    const root = document.body || document.documentElement;
    if (!root) return;

    observer.observe(root, {
      childList: true,
      subtree: true
    });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }


  document.addEventListener(
    'dialog:after-show',
    event => retryTarget(event.target || document),
    true
  );


  document.addEventListener(
    'click',
    event => {
      const trigger = event.target.closest?.(
        '[aria-controls*="quick-buy"],' +
        '[aria-controls*="quick-view"],' +
        '.product-card__quick-add-button'
      );

      if (!trigger) return;

      const id = trigger.getAttribute('aria-controls');
      const target = id ? document.getElementById(id) : null;

      if (target) {
        retryTarget(target);
      }
    },
    true
  );

})();
